import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Mountain, HardHat, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const MiningQuarrying = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/mining-quarrying"]?.title} description={seoData["industries/mining-quarrying"]?.description} path="/industries/mining-quarrying" keywords={seoData["industries/mining-quarrying"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Mining & Quarrying"
        description="Erosion and corrosion protection for mining and quarrying operations. Abrasion-resistant coatings for hoppers, chutes, cyclones, conveyors, and processing equipment."
        backgroundKey="mining-quarrying"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Severe erosion and abrasion damage on chutes, hoppers, and transfer points',
              'Cyclone and hydrocyclone wear from slurry processing',
              'Conveyor belt support and structure corrosion',
              'Process water pipe erosion from abrasive particles',
              'Pump impeller and casing erosion',
              'Concrete foundation and containment deterioration'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-stone-100 rounded-lg">
                <Mountain className="text-stone-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-stone-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <HardHat className="text-stone-600 mr-3 flex-shrink-0" size={22} />
                  Chute & Hopper Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Ceramic-filled and rubber-based lining systems for chutes, hoppers, bins, and transfer points. Abrasion-resistant coatings that withstand impact and sliding wear from ore, aggregate, and minerals.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-stone-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <HardHat className="text-stone-600 mr-3 flex-shrink-0" size={22} />
                  Cyclone & Process Equipment Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Internal lining and erosion repair for hydrocyclones, classifiers, flotation cells, and thickeners. Extend equipment life between scheduled maintenance shutdowns.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-stone-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <HardHat className="text-stone-600 mr-3 flex-shrink-0" size={22} />
                  Pipe Erosion Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Internal pipe coating and repair for slurry lines, tailings pipes, and process water systems. Composite pipe wraps for external corrosion and structural reinforcement.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-stone-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <HardHat className="text-stone-600 mr-3 flex-shrink-0" size={22} />
                  Structural & Concrete Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Foundation repair, containment area coating, and structural steel protection for crushing plants, screening facilities, and processing buildings.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Mining & Quarrying?</h2>
              <div className="space-y-3">
                {['Extreme abrasion resistance — ceramic and rubber compound systems',
                  'Fast turnaround for shutdown and outage work',
                  'Reduce equipment replacement frequency and costs',
                  'Extend crusher, screen, and conveyor component life',
                  'On-site mobile blasting and surface preparation',
                  'Emergency repair capabilities for production-critical equipment'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-stone-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Mining Equipment Specialists</h3>
              <p className="text-stone-200 mb-6">BCS understands the extreme wear demands of mining and quarrying operations. Our abrasion-resistant coating systems extend equipment life in the harshest environments.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/equipment-repair')}>
                View Equipment Repair <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Mining Equipment</h2>
          <p className="text-blue-100 mb-8">Contact us for an erosion and corrosion assessment at your mine or quarry operation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MiningQuarrying;
